/**
 * The page's half of the terms record (2026-10-12, SOC 2 codebase audit, docs/compliance/soc2-codebase-audit-report.md
 * §5). After a NEW account is created with the terms box ticked, tell the server, which keeps the record where no
 * browser can change it (functions/src/legal/termsConsent.ts → users/{uid}/terms_consents).
 *
 * The versions are the effective dates of the Terms of Service and the Privacy Policy this page shows
 * (src/config/legalVersions.ts), so the record names the documents the person was offered.
 *
 * It never holds sign-in up — the person is already in. Each call is bounded; two more attempts follow in this tab;
 * after that the record waits in this browser (only for that account) and the next signed-in page load sends it
 * (TermsUpdateHost on every page since SOC 2 Phase 1, 2026-10-13; before, only ProtectedRoute in src/App.tsx), so a
 * closed tab or a dropped network does not lose it.
 *
 * Once the server has kept a record of the CURRENT documents, this browser remembers it for that account
 * (hasCachedTermsAcceptance), so the terms dialog's host makes no read at all on later page loads.
 */
import { LEGAL_VERSIONS } from '../../config/legalVersions';
import type { TermsConsentSignInMethod, TermsConsentSurface } from './termsConsentVocabulary';

export type { TermsConsentSurface } from './termsConsentVocabulary';
/** How the person signs in (the vocabulary's sign-in methods). */
export type TermsConsentMethod = TermsConsentSignInMethod;

export interface TermsConsentPayload {
  tosVersion: string;
  privacyVersion: string;
  surface: TermsConsentSurface;
  signInMethod: TermsConsentMethod;
  acceptedAtMs: number;
}

interface PendingTermsConsent extends TermsConsentPayload {
  uid: string;
}

/** Where a record that could not be sent waits, in this browser only. */
export const PENDING_TERMS_CONSENT_KEY = 'prest0.termsConsent.pending';
/** This browser's memory, per account, that the server kept a record of the current documents. */
export const TERMS_ACCEPTED_KEY_PREFIX = 'prest0.termsConsent.accepted:';

/** The current documents, as one value: the Terms of Service's effective date, then the Privacy Policy's. */
export function currentTermsVersions(): string {
  return `${LEGAL_VERSIONS.terms.effectiveDate}|${LEGAL_VERSIONS.privacy.effectiveDate}`;
}

/** Has the server kept this account's agreement to the CURRENT documents, as far as this browser knows? */
export function hasCachedTermsAcceptance(uid: string): boolean {
  try {
    return Boolean(uid) && localStorage.getItem(TERMS_ACCEPTED_KEY_PREFIX + uid) === currentTermsVersions();
  } catch {
    return false;
  }
}

/** Remember that the server kept this account's agreement to the current documents. */
export function cacheTermsAcceptance(uid: string): void {
  try {
    if (uid) localStorage.setItem(TERMS_ACCEPTED_KEY_PREFIX + uid, currentTermsVersions());
  } catch { /* storage unavailable: the host reads the record again next time, which is only one read */ }
}
const CALL_TIMEOUT_MS = 8_000;
const RETRY_DELAYS_MS = [2_000, 6_000];

/** What the server is told: the documents this page shows, the screen, the door, and when the box was ticked. */
export function termsConsentPayload(
  surface: TermsConsentSurface,
  signInMethod: TermsConsentMethod,
  acceptedAtMs: number,
): TermsConsentPayload {
  return {
    tosVersion: LEGAL_VERSIONS.terms.effectiveDate,
    privacyVersion: LEGAL_VERSIONS.privacy.effectiveDate,
    surface,
    signInMethod,
    acceptedAtMs,
  };
}

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('terms-consent-timeout')), ms);
    promise.then(
      (value) => { clearTimeout(timer); resolve(value); },
      (error) => { clearTimeout(timer); reject(error); },
    );
  });
}

/** One call to the server. True when it kept the record. */
async function sendOnce(payload: TermsConsentPayload): Promise<boolean> {
  try {
    const { getFunctions, httpsCallable } = await import('firebase/functions');
    const record = httpsCallable(getFunctions(undefined, 'us-central1'), 'recordTermsConsent');
    const result = await withTimeout(record(payload), CALL_TIMEOUT_MS);
    return Boolean((result as { data?: { ok?: unknown } })?.data?.ok);
  } catch {
    return false;
  }
}

function readPending(): PendingTermsConsent | null {
  try {
    const raw = localStorage.getItem(PENDING_TERMS_CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PendingTermsConsent>;
    return typeof parsed?.uid === 'string' && typeof parsed.tosVersion === 'string' ? (parsed as PendingTermsConsent) : null;
  } catch {
    return null;
  }
}

function writePending(pending: PendingTermsConsent | null): void {
  try {
    if (pending) localStorage.setItem(PENDING_TERMS_CONSENT_KEY, JSON.stringify(pending));
    else localStorage.removeItem(PENDING_TERMS_CONSENT_KEY);
  } catch { /* storage unavailable: the in-tab attempts are all there is */ }
}

/**
 * Send the record for the account just created: up to three attempts in this tab, then it waits in this browser for
 * the next signed-in page load. Resolves true once the server kept it.
 */
export async function sendTermsConsent(input: {
  uid: string;
  surface: TermsConsentSurface;
  signInMethod: TermsConsentMethod;
  acceptedAtMs: number;
  sleep?: (ms: number) => Promise<void>;
}): Promise<boolean> {
  const sleep = input.sleep ?? ((ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms)));
  const payload = termsConsentPayload(input.surface, input.signInMethod, input.acceptedAtMs);
  writePending({ uid: input.uid, ...payload });
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt += 1) {
    if (attempt > 0) await sleep(RETRY_DELAYS_MS[attempt - 1]);
    if (await sendOnce(payload)) {
      writePending(null);
      cacheTermsAcceptance(input.uid);
      return true;
    }
  }
  return false;
}

/** Is a record waiting in this browser for this account (sent, not yet confirmed)? */
export function hasPendingTermsConsentFor(uid: string): boolean {
  const pending = readPending();
  return Boolean(pending && uid && pending.uid === uid);
}

let flushing: { uid: string; promise: Promise<boolean> } | null = null;

/** A record that waited in this browser goes now, if it is this account's. Anything else is left alone. */
export function flushPendingTermsConsent(uid: string): Promise<boolean> {
  // One send per account at a time: two calls in this tab must not file the same agreement twice.
  if (flushing && flushing.uid === uid) return flushing.promise;
  const promise = flushOnce(uid).finally(() => { if (flushing?.promise === promise) flushing = null; });
  flushing = { uid, promise };
  return promise;
}

async function flushOnce(uid: string): Promise<boolean> {
  const pending = readPending();
  if (!pending || pending.uid !== uid) return false;
  const { uid: _owner, ...payload } = pending;
  if (await sendOnce(payload)) {
    writePending(null);
    if (payload.tosVersion === LEGAL_VERSIONS.terms.effectiveDate && payload.privacyVersion === LEGAL_VERSIONS.privacy.effectiveDate) {
      cacheTermsAcceptance(uid);
    }
    return true;
  }
  return false;
}
